import { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { ArrowLeft, ExternalLink, Printer } from 'lucide-react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { PAGES } from 'constants/pages';

import { BoxState, GetDeliveryQuery, useUpdateDeliveryMutation, TotadminPermission } from 'data/__generated__';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { SectionContainer } from 'components/DetailsView/Section';
import {
    ActionsAndSave,
    PageActions,
    PageBreadcrumb,
    PageBreadcrumbSegment,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { UpdateStockConfirmationModal } from 'components/UpdateStockConfirmationModal';
import { BoxesContentForm } from 'pages/Bundles/components/BoxesContentForm';
import { PhotosSection as SiteSetupPhotoSection } from 'pages/SiteSetups/details/PhotosSection';

import { formatDateForDisplay } from 'helpers/dateTimes';

import { DeliveryFormData } from '../constants/types';
import { useOrderDeliveryNote } from '../components/OrderDeliveryNote';
import { DeliveryHistorySection } from './DeliveryHistorySection';
import { DeliveryKeyFacts } from './DeliveryKeyFacts';
import { DeliveryStateCard } from './DeliveryStateCard';
import { OrdersSection } from './OrdersSection';
import { PhotosSectionContent } from './PhotosSectionContent';
import { StockTransfersSection } from './StockTransfersSection';
import { useDeliveryStateActions } from './useDeliveryStateActions';

type Delivery = NonNullable<GetDeliveryQuery['delivery']>;

export const DeliveryDetailsForm = ({ delivery }: { delivery: Delivery }) => {
    const methods = useForm<DeliveryFormData>();
    const {
        formState: { isDirty },
        reset,
    } = methods;
    const [updateDelivery, { loading: updateDeliveryLoading }] = useUpdateDeliveryMutation();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    const { actions: stateActions, loading: stateActionLoading } = useDeliveryStateActions({
        deliveryId: delivery._id,
        state: delivery.state,
        hasUnsavedChanges: isDirty,
    });
    const { print: printDeliveryNote, printableNode } = useOrderDeliveryNote(delivery);

    useEffect(() => {
        methods.reset(delivery);
    }, [delivery, methods]);

    const hasQuantityPickedChanges = (): boolean => {
        const initialBoxes = delivery.boxes || [];
        const formBoxes = methods.getValues().boxes || [];

        // Fonction pour créer une map avec boxId-productId comme clé et quantityPicked comme valeur (en excluant 0 et null)
        const getQuantityPickedMap = (
            boxes: Array<{
                _id: string;
                products?: Array<{ _id: string; quantityPicked?: number | null }> | null;
            }>,
        ): Map<string, number> => {
            const quantityMap = new Map<string, number>();

            for (const box of boxes) {
                for (const product of box.products || []) {
                    const quantityPicked = product.quantityPicked;
                    // Exclure null, undefined et 0
                    if (quantityPicked !== null && quantityPicked !== undefined && quantityPicked !== 0) {
                        const key = `${box._id}-${product._id}`;
                        quantityMap.set(key, quantityPicked);
                    }
                }
            }

            return quantityMap;
        };

        // Boxes staged for removal (state Aborted) or bundle detachment (bundleId nulled) get their
        // quantities reset server-side → treat them as removed so the stock-update modal triggers.
        const initialBundleIdByBoxId = new Map(initialBoxes.map((box) => [box._id, box.bundleId]));
        const activeFormBoxes = formBoxes.filter(
            (box) =>
                box.state !== BoxState.Aborted &&
                !(box.bundleId == null && initialBundleIdByBoxId.get(box._id) != null),
        );

        const initialMap = getQuantityPickedMap(initialBoxes);
        const formMap = getQuantityPickedMap(activeFormBoxes);

        // Étape 1 : Comparer les clés avec un Set
        const initialKeys = new Set(initialMap.keys());
        const formKeys = new Set(formMap.keys());

        // Vérifier si les sets de clés sont différents
        if (initialKeys.size !== formKeys.size || ![...initialKeys].every((value) => formKeys.has(value))) {
            return true;
        }

        // Étape 2 : Comparer les valeurs clé par clé
        for (const [key, initialValue] of initialMap.entries()) {
            const formValue = formMap.get(key);
            if (formValue === undefined || initialValue !== formValue) {
                return true;
            }
        }

        return false;
    };

    const handleUpdateButton = async () => {
        if (hasQuantityPickedChanges()) {
            setIsConfirmationModalOpen(true);
        } else {
            // Aucune modification de quantityPicked, appeler directement avec shouldUpdateStocks=false
            await handleConfirm(false);
        }
    };

    const handleConfirm = async (shouldUpdateStocks: boolean) => {
        const deliveryFormData = methods.getValues();

        try {
            // bundleId/state carry the staged actions: the API detaches the box from its bundle when
            // bundleId turns null, and removes it from the delivery when state turns Aborted.
            const formattedBoxes = deliveryFormData.boxes.map((box) => ({
                _id: box._id,
                code: box.code,
                bundleId: box.bundleId ?? null,
                state: box.state,
                products: box.products.map((product) => ({
                    _id: product._id,
                    quantity: Number(product.quantity),
                    quantityPicked: product.quantityPicked !== null ? Number(product.quantityPicked) : null,
                    unitPrice: product.unitPrice !== null ? Number(product.unitPrice) : null,
                })),
            }));

            const { data } = await updateDelivery({
                variables: {
                    delivery: {
                        _id: delivery._id,
                        state: deliveryFormData.state,
                        boxes: formattedBoxes,
                    },
                    shouldUpdateStocks,
                },
            });

            if (data?.updateDelivery.delivery) {
                toast.success('La livraison a bien été mise à jour');
                reset(data.updateDelivery.delivery);
            }
        } catch (error) {
            toast.error('Une erreur est survenue lors de la mise à jour de la livraison');
        }

        setIsConfirmationModalOpen(false);
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Documents',
            items: [{ label: 'Imprimer le bon de livraison', icon: <Printer />, onClick: () => printDeliveryNote() }],
        },
        {
            label: 'Liens',
            items: [
                // Une livraison de commande freefood n'a PAS de site (`Delivery.siteId` est nullable) :
                // proposer le lien donnait un `/site/null`. Même garde que `DeliveryHistorySection`.
                ...(delivery.siteId
                    ? [
                          {
                              label: 'Fiche du site',
                              icon: <ExternalLink />,
                              to: `/site/${delivery.siteId}`,
                              requiredPermissions: [TotadminPermission.SitesRead],
                          },
                      ]
                    : []),
                ...(delivery.siteSetup
                    ? [
                          {
                              label: 'Rangement Totops',
                              icon: <ExternalLink />,
                              to: `/site-setup/${delivery.siteSetup._id}`,
                              requiredPermissions: [TotadminPermission.SiteSetupsRead],
                          },
                      ]
                    : []),
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.deliveries.url }],
        },
    ];

    return (
        <Container>
            <Helmet title={`${delivery.label} · Livraison · TOTADMIN`} />
            <FormProvider {...methods}>
                <ContentWrapper>
                    <PageHeader>
                        <PageBreadcrumb>
                            <PageBreadcrumbSegment label="Livraisons" to={PAGES.deliveries.url} />
                            <PageBreadcrumbSegment
                                label={delivery.label}
                                kicker={formatDateForDisplay(delivery.deliveryDate, { displayDay: true })}
                                isCurrent
                                dataTest="delivery-breadcrumb"
                            />
                        </PageBreadcrumb>
                        <PageActions>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={handleUpdateButton}
                                saveDisabled={!isDirty}
                                saveLoading={updateDeliveryLoading}
                                saveTitle="Mettre à jour"
                                saveRequiredPermissions={[TotadminPermission.DeliveriesManage]}
                            />
                        </PageActions>
                    </PageHeader>
                    <Content>
                        <ScrollableContent>
                            <DeliveryStateCard
                                state={delivery.state}
                                actions={stateActions}
                                loading={stateActionLoading}
                            />
                            <DeliveryKeyFacts delivery={delivery} />
                            <DeliveryHistorySection delivery={delivery} />
                            <StockTransfersSection delivery={delivery} />
                            <OrdersSection delivery={delivery} />
                            {delivery.siteSetup ? (
                                <SiteSetupPhotoSection siteSetup={delivery.siteSetup} />
                            ) : (
                                <SectionContainer title="Photos" isInitiallyOpen>
                                    <PhotosSectionContent delivery={delivery} />
                                </SectionContainer>
                            )}
                            <BoxesContentForm filterBy="sequence" />
                            {/* Les caisses avec la livraison : leur contenu s'édite depuis CETTE page,
                                et une écriture sur une caisse n'écrit rien sur la livraison. */}
                            <AuditLogsSection
                                collectionDocumentIds={[delivery._id, ...delivery.boxes.map(({ _id }) => _id)]}
                                subjectLabels={Object.fromEntries(
                                    delivery.boxes.map(({ _id, number }) => [_id, `#${number}`]),
                                )}
                            />
                        </ScrollableContent>
                    </Content>
                </ContentWrapper>
            </FormProvider>

            {printableNode}

            <UpdateStockConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={() => {
                    setIsConfirmationModalOpen(false);
                }}
                onConfirm={(shouldUpdateStocks) => handleConfirm(shouldUpdateStocks)}
                isLoading={updateDeliveryLoading}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;
